import React, { useRef, useState, useEffect } from 'react';
import './LandingPage.css';
import LandingNav from '../../components/LandingPage/LandingNav/LandingNav';
import LandingHero from '../../components/LandingPage/LandingHero/LandingHero';
import LandingCTA from '../../components/LandingPage/LandingCTA/LandingCTA';
import LandingFooter from '../../components/LandingPage/LandingFooter/LandingFooter';
import AudienceSection from '../../components/LandingPage/AudienceSection/AudienceSection';
import LandingFAQ from '../../components/LandingPage/LandingFAQ/LandingFAQ';

// Import visuals
import visual01 from '../../image-assets/visual_01.png';
import visual02 from '../../image-assets/visual_02.png';
import visual03 from '../../image-assets/visual_03.png';
import visual04 from '../../image-assets/visual_04.png';
// @ts-ignore
import iconAudioOn from '../../image-assets/icon.audio.on.svg';
// @ts-ignore
import iconAudioOff from '../../image-assets/icon.audio.off.svg';
// @ts-ignore
import iconRephrase from '../../image-assets/icon.rephrase.svg';
// @ts-ignore
import iconPlay from '../../image-assets/icon.play.svg';
// @ts-ignore
import iconPause from '../../image-assets/icon.pause.svg';

// Define features for each section - Concise Feature Descriptions
const writerFeatures = [
  { title: "Effortless Polishing", description: "Fix grammar, improve clarity, and ensure consistency automatically.", docsId: "tone-of-voice" },
  { title: "Perfect Tone, Every Time", description: "Adapt your writing tone for any audience, culture, or style.", docsId: "tone-of-voice" },
  { title: "Writing Assistant", description: "Get smart suggestions to rephrase, shorten, or expand text.", docsId: "ai-translate" }
];

const localizationFeatures = [
  { title: "Fast AI Translation", description: "Instantly translate unlimited strings using state-of-the-art AI.", docsId: "ai-translate" },
  { title: "Managed Reviews", description: "Collaborate on feedback and approvals for high-quality translations.", docsId: "reviews" },
  { title: "Broad Platform Support", description: "Export ready-to-use files for iOS, Android, Web, and other platforms.", docsId: "export" }
];

const developerFeatures = [
  { title: "Easy file Import/Export", description: "Easily import/export files like JSON, XML, .strings, and more.", docsId: "import" },
  { title: "API & Automation", description: "Automate translation workflows and integrate with your CI/CD pipeline.", docsId: "api" },
  { title: "Universal Placeholders", description: "Define platform-agnostic placeholders that export correctly everywhere.", docsId: "placeholders" }
];

const teamFeatures = [
  { title: "Team Collaboration", description: "Invite members, assign roles, and collaborate in real-time.", docsId: "collaboration" },
  { title: "Organized Workflow", description: "Track status, assign tasks, and manage localization deadlines.", docsId: "reviews" },
  { title: "Consistent Brand Voice", description: "Maintain consistent terminology and style across all languages.", docsId: "tone-of-voice" }
];

const LandingPage = () => {
  const landingVideoRef = useRef<HTMLVideoElement>(null);
  const landingVideoWrapperRef = useRef<HTMLDivElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [showReplayButton, setShowReplayButton] = useState(false);
  const [replayButtonExiting, setReplayButtonExiting] = useState(false);
  const [replaySpinning, setReplaySpinning] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const showReplayButtonRef = useRef(showReplayButton);
  showReplayButtonRef.current = showReplayButton;

  const progressRingRadius = 25;
  const progressRingCircumference = 2 * Math.PI * progressRingRadius;
  const progressRingOffset = progressRingCircumference * (1 - videoProgress);

  useEffect(() => {
    const wrapper = landingVideoWrapperRef.current;
    const video = landingVideoRef.current;
    if (!wrapper || !video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) {
          video.pause();
          return;
        }
        video.muted = true;
        setVideoMuted(true);
        video.play().catch(() => {});
      },
      { threshold: 1 }
    );
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page">
      <LandingNav />

      {/* Use LandingHero component */}
      <LandingHero />

      <section className="landing-video-section">
        <div className="landing-video-wrapper" ref={landingVideoWrapperRef}>
          <video
            ref={landingVideoRef}
            src="/Texta%20Demo%20Web.mp4"
            playsInline
            onPlay={() => {
              setVideoPlaying(true);
              if (showReplayButtonRef.current) setReplayButtonExiting(true);
              else setShowReplayButton(false);
            }}
            onPause={() => {
              setVideoPlaying(false);
              const v = landingVideoRef.current;
              setShowReplayButton(Boolean(v && v.currentTime > 0.1));
            }}
            onEnded={() => {
              setVideoPlaying(false);
              setShowReplayButton(true);
            }}
            onTimeUpdate={() => {
              const v = landingVideoRef.current;
              if (v && v.duration > 0) setVideoProgress(v.currentTime / v.duration);
            }}
            onLoadedMetadata={() => {
              const v = landingVideoRef.current;
              if (v && v.duration > 0) setVideoProgress(v.currentTime / v.duration);
            }}
          >
            Your browser does not support the video tag.
          </video>
          <div className="landing-video-controls">
            <div className="landing-video-controls-left">
              <div className="landing-video-play-wrapper">
                <svg className="landing-video-progress-ring" viewBox="0 0 54 54" aria-hidden>
                  <circle
                    className="landing-video-progress-ring-fill"
                    cx="27"
                    cy="27"
                    r={progressRingRadius}
                    fill="none"
                    strokeWidth="2"
                    strokeDasharray={progressRingCircumference}
                    strokeDashoffset={progressRingOffset}
                    strokeLinecap="round"
                    transform="rotate(-90 27 27)"
                  />
                </svg>
                <button
                  type="button"
                  className="landing-video-control"
                  onClick={() => {
                    const v = landingVideoRef.current;
                    if (v) v.paused ? v.play() : v.pause();
                  }}
                  aria-label={videoPlaying ? 'Pause' : 'Play'}
                >
                  <img
                    src={videoPlaying ? iconPause : iconPlay}
                    alt=""
                    className="landing-video-control-icon"
                    aria-hidden
                  />
                </button>
              </div>
              {(showReplayButton || replayButtonExiting) && (
                <button
                  type="button"
                  className={`landing-video-control landing-video-control-replay${replayButtonExiting ? ' landing-video-control-replay-exit' : ''}${replaySpinning ? ' landing-video-control-replay-spin' : ''}`}
                  onClick={() => {
                    if (replayButtonExiting || replaySpinning) return;
                    setReplaySpinning(true);
                  }}
                  aria-label="Replay"
                  onAnimationEnd={(e) => {
                    if (e.animationName === 'landing-video-replay-out') {
                      setShowReplayButton(false);
                      setReplayButtonExiting(false);
                    }
                    if (e.animationName === 'landing-video-replay-spin') {
                      const v = landingVideoRef.current;
                      if (v) {
                        v.currentTime = 0;
                        v.play();
                      }
                      setReplaySpinning(false);
                    }
                  }}
                >
                  <img
                    src={iconRephrase}
                    alt=""
                    className="landing-video-control-icon landing-video-control-icon-white landing-video-control-icon-flip"
                    aria-hidden
                  />
                </button>
              )}
            </div>
            <button
              type="button"
              className="landing-video-control"
              onClick={() => {
                const v = landingVideoRef.current;
                if (v) {
                  v.muted = !v.muted;
                  setVideoMuted(v.muted);
                }
              }}
              aria-label={videoMuted ? 'Unmute' : 'Mute'}
            >
              <img
                src={videoMuted ? iconAudioOff : iconAudioOn}
                alt=""
                className="landing-video-control-icon"
                aria-hidden
              />
            </button>
          </div>
        </div>
      </section>

      {/* Audience-focused Sections with IDs */}
      <AudienceSection
        id="writers"
        title="Effortless writing & editing"
        description="Fix grammar, refine tone, and polish your copy effortlessly. Let AI enhance your writing so every word matters."
        features={writerFeatures}
        visualContent={<img src={visual01} alt="Texta editor showing writing and editing features" />}
      />

      <AudienceSection
        id="localization"
        title="Accurate translation, no revisions"
        description="Get accurate AI translations quickly, then fine-tune your message. Manage all languages without the headache."
        features={localizationFeatures}
        visualContent={<img src={visual02} alt="Texta editor showing translation features" />}
      />

      <AudienceSection
        id="developers"
        title="Fits your development workflow"
        description="Import existing files, automate with the API, and forget platform-specific formatting."
        features={developerFeatures}
        visualContent={<img src={visual03} alt="Texta editor showing developer-friendly features like API and file export" />}
      />

       <AudienceSection
         id="teams"
         title="Collaborate effectively on content"
         description="Great copy is a team effort. Bring everyone together to manage projects and craft the perfect message, together."
         features={teamFeatures}
         visualContent={<img src={visual04} alt="Texta editor showing team collaboration features" />}
       />

      {/* Use LandingCTA component */}
      <LandingCTA />

      {/* Add LandingFAQ component */}
      <LandingFAQ />

      {/* Use LandingFooter component */}
      <LandingFooter />
    </div>
  );
};

export default LandingPage; 